import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  Text,
  DropDownSelect,
  BookTeeTimeCard,
  DateInput,
  Loader,
} from "@components";
import { checkDatePast } from "@utils";
import { getClubCachedTeeTime } from "@redux/teetime/actions";
import { getListingBookings } from "@redux/booking/actions";

const BookDestinationsCard = ({
  destination,
  date = null,
  handleDateChange = () => {},
  goToCoursesDetails = () => {},
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { logo } = useSelector((state) => state.headerReducer);
  const logoPath = logo?.data?.logo?.url;
  const [state, setState] = useState({
    extend: false,
    isTooltip: false,
    datePickerOpen: false,
    selectedCourse: destination?.courses[0],
    teeTimesList: null,
    loading: false,
    dateLock: false,
  });
  const {
    extend,
    isTooltip,
    datePickerOpen,
    selectedCourse,
    teeTimesList,
    loading,
    dateLock,
  } = state;

  const handleStopPropagation = (e) => e.stopPropagation();

  const handleToggleToolTip = (e) =>
    setState((prevState) => ({ ...prevState, isTooltip: e }));

  const handleGoToCoursesDetails = (e) =>
    !isTooltip && !datePickerOpen && goToCoursesDetails();

  const getTeeTimeListApi = async (
    courseId = destination?.courses?.[0]?.courseId || selectedCourse?.courseId,
    apiDate = date
  ) => {
    setState((prevState) => ({
      ...prevState,
      loading: true,
      teeTimesList: null,
    }));

    try {
      const apiData = {
        clubId: destination.id,
        bookingDate: dayjs(apiDate).format("DD-MM-YYYY"),
        holes: 18,
        players: 1,
        courseId,
      };

      const teeTimeRes = await dispatch(getClubCachedTeeTime(apiData));

      if (
        teeTimeRes?.payload?.status !== 200 ||
        !Array.isArray(teeTimeRes?.payload?.data?.data) ||
        teeTimeRes?.payload?.data?.data?.length === 0
      ) {
        if (teeTimeRes?.payload?.response?.data?.error?.status === 404) {
          toast.error(t("course.no_Tee"));
          setState((prevState) => ({
            ...prevState,
            loading: false,
          }));
        } else {
          toast.error(
            teeTimeRes?.payload?.response?.data?.error?.message ||
              "A generic error occurred on the tee time. Please refresh and try again."
          );
          setState((prevState) => ({
            ...prevState,
            loading: false,
          }));
        }
        return;
      }

      let teeTimeList = teeTimeRes.payload.data.data.filter(
        (item) => !checkDatePast(item?.time)
      );

      const fromDate = dayjs(apiDate).subtract(1, "day").format("DD-MM-YYYY");
      const toDate = dayjs(apiDate).add(1, "day").format("DD-MM-YYYY");

      const bookingRes = await dispatch(
        getListingBookings({ fromDate, toDate })
      );

      let updatedTeeTimes = [...teeTimeList];

      if (
        bookingRes?.payload?.status === 200 &&
        Array.isArray(bookingRes?.payload?.data?.data?.data)
      ) {
        const listingData = bookingRes.payload.data.data.data;

        const groupedBookings = listingData.reduce((acc, item) => {
          if (item.status === "BOOKED") {
            const key = `${item.facilityId}_${item.course}_${item.teetime}`;
            if (!acc[key]) {
              acc[key] = { ...item };
            } else {
              acc[key].players += item.players;
            }
          }
          return acc;
        }, {});

        const groupedBookingsArray = Object.values(groupedBookings);

        updatedTeeTimes = teeTimeList.map((item) => {
          const matchingBookings = groupedBookingsArray.filter(
            (booking) =>
              booking.teetime === item.time &&
              booking.course === item.course &&
              booking.facilityId === destination.id
          );

          const totalPlayers = matchingBookings.reduce(
            (sum, booking) => sum + booking.players,
            0
          );

          if (totalPlayers > 0) {
            return {
              ...item,
              products: [
                {
                  ...item.products?.[0],
                  available: Math.max(
                    0,
                    Math.min(
                      4 - (totalPlayers || 0),
                      item.products?.[0]?.available
                    )
                  ),
                },
              ],
            };
          }
          return item;
        });
      }

      const updatedTeeData = updatedTeeTimes.filter(
        (item) => item.products?.[0]?.available > 0
      );

      setState((prevState) => ({
        ...prevState,
        teeTimesList: updatedTeeData,
        dateLock: updatedTeeData.length > 0,
        loading: false,
      }));
    } catch (error) {
      toast.error("Something went wrong. Please try again later.");
      setState((prevState) => ({
        ...prevState,
        loading: false,
      }));
    }
  };

  const handleChangeCourses = (e) => {
    setState((prevState) => ({
      ...prevState,
      selectedCourse: e,
      extend: true,
    }));
    getTeeTimeListApi(e?.courseId);
  };

  const onDateChange = (e) => {
    destination?.courses.length > 0 &&
      setState((prevState) => ({ ...prevState, extend: true }));
    handleDateChange(e);
    destination?.courses.length > 0 &&
      getTeeTimeListApi(selectedCourse?.courseId, e);
  };

  const handleClickButton = (e) => {
    e.stopPropagation();
    destination?.courses.length > 0 && getTeeTimeListApi();
    destination?.courses.length > 0 &&
      setState((prevState) => ({ ...prevState, extend: !prevState.extend }));
  };

  const handleTryAgain = (e) => {
    e.stopPropagation();
    setState((prevState) => ({ ...prevState, extend: true }));
    getTeeTimeListApi();
  };

  return (
    <div className="flex flex-col lg:flex-row bg-white w-full rounded-lg shadow-[0_0px_15px_0px_#cdcaec]">
      <div className="flex flex-col items-center lg:w-1/3 border-b lg:border-b-0 lg:border-r border-blackText py-4 px-6 text-center gap-4">
        <div
          onClick={handleGoToCoursesDetails}
          className="hover:cursor-pointer"
        >
          <Text className="text-[18px] text-blackText font-bold line-clamp-1">
            {destination?.name}
          </Text>
        </div>
        <div
          onClick={handleGoToCoursesDetails}
          className="flex w-full h-28 hover:cursor-pointer rounded-lg"
        >
          <img
            src={destination?.logo || destination?.image?.[0]?.url || logoPath}
            onError={(e) =>
              (e.target.src = destination?.image?.[0]?.url || logoPath)
            }
            className="object-contain h-28 w-full rounded-lg"
            alt={""}
          />
        </div>
      </div>
      <div onClick={handleStopPropagation} className="flex flex-col w-full p-4">
        <div className="flex w-full gap-10 mb-8 flex-col lg:flex-row">
          <div className="flex lg:w-[50%]">
            <DropDownSelect
              label={t("book.selectCourse")}
              noDataMessage={t("book.noCourseAvailable")}
              handleChange={(e) => handleChangeCourses(e)}
              value={selectedCourse}
              options={destination?.courses}
            />
          </div>
          <div className="flex lg:w-[50%]">
            <DateInput
              label={t("book.selectDate")}
              placeholder={t("book.selectDate")}
              handleChange={(e) => onDateChange(e)}
              value={date}
              lock={dateLock}
              isOpen={datePickerOpen}
              onDatePickerClose={() =>
                setState((prevState) => ({
                  ...prevState,
                  datePickerOpen: false,
                }))
              }
              onDatePickerOpen={() =>
                setState((prevState) => ({
                  ...prevState,
                  datePickerOpen: true,
                }))
              }
              isDisabled={destination?.courses.length < 1}
            />
          </div>
        </div>
        {loading && <Loader />}
        {extend ? (
          teeTimesList?.length ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 max-h-[400px] overflow-auto custom-scroll md:pr-2">
              {teeTimesList?.map((item, index) => (
                <BookTeeTimeCard
                  key={index}
                  teetime={item}
                  destination={destination}
                  onToggleToolTip={handleToggleToolTip}
                />
              ))}
            </div>
          ) : (
            !loading && (
              <div className="text-center flex flex-col justify-center gap-2">
                <Text className="text-[16px] font-bold text-blackText">
                  {t("course.no_Tee")}
                </Text>
                <div
                  onClick={handleTryAgain}
                  className="flex mx-auto hover:cursor-pointer"
                >
                  <Text className="text-[16px] font-bold text-buttonColor">
                    {t("course.try_Again")}
                  </Text>
                </div>
              </div>
            )
          )
        ) : (
          <div
            onClick={handleClickButton}
            className={`mx-auto h-[40px] rounded-md flex flex-row items-center justify-around px-10 hover:opacity-80 ${
              destination?.courses.length > 0
                ? "bg-buttonColor hover:cursor-pointer"
                : "bg-midGray hover:cursor-not-allowed"
            }`}
          >
            <Text className="text-buttonTextColor text-center font-extrabold">
              {destination?.courses.length > 0
                ? t("book.showTeeTime")
                : t("book.noCourseAvailable")}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookDestinationsCard;
